/* Shopify storefront enhancements. Product forms submit to Shopify without creating orders here. */
(() => {
  const parseJSON = element => {
    try { return JSON.parse(element?.textContent || '{}'); } catch { return {}; }
  };
  const validHandle = handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);

  if (!customElements.get('drako-store-favorites')) {
    customElements.define('drako-store-favorites', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        this.key = 'drako:favorites:v1';
        this.config = parseJSON(this.querySelector('[data-drako-favorites-config]'));
        this.dialog = this.querySelector('dialog');
        this.list = this.querySelector('[data-drako-favorites-list]');
        this.status = this.querySelector('[data-drako-favorites-status]');
        this.feedback = this.querySelector('[data-drako-favorites-feedback]');
        this.handles = this.readSaved();
        this.syncButtons();
        document.querySelectorAll('[data-drako-favorites-open]').forEach(button => button.removeAttribute('hidden'));
        const signal = this.controller.signal;
        document.addEventListener('click', event => {
          const button = event.target instanceof Element ? event.target.closest('[data-drako-favorite-toggle]') : null;
          if (button && validHandle(button.dataset.productHandle)) this.toggle(button.dataset.productHandle);
          const opener = event.target instanceof Element ? event.target.closest('[data-drako-favorites-open]') : null;
          if (opener && this.dialog && typeof this.dialog.showModal === 'function') {
            this.origin = opener;
            this.dialog.showModal();
            this.querySelector('[data-drako-favorites-close]')?.focus();
            this.loadSaved();
          }
        }, { signal });
        this.addEventListener('click', event => {
          if (!(event.target instanceof Element)) return;
          if (event.target.closest('[data-drako-favorites-close]')) this.dialog?.close();
          if (event.target === this.dialog) {
            const rect = this.dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
          }
        }, { signal });
        this.dialog?.addEventListener('close', () => {
          this.requestController?.abort();
          if (this.origin?.isConnected) this.origin.focus();
        }, { signal });
        document.addEventListener('drako:cards-ready', () => this.syncButtons(), { signal });
        window.addEventListener('storage', event => {
          if (event.key !== this.key) return;
          this.handles = this.readSaved();
          this.syncButtons();
          if (this.dialog?.open) this.loadSaved();
        }, { signal });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.requestController?.abort();
        this.controller = null;
      }
      readSaved() {
        try {
          const stored = JSON.parse(localStorage.getItem(this.key) || '[]');
          return Array.isArray(stored) ? [...new Set(stored.filter(validHandle))].slice(0, 40) : [];
        } catch { return this.handles || []; }
      }
      persist() {
        try { localStorage.setItem(this.key, JSON.stringify(this.handles)); return true; }
        catch { return false; }
      }
      label(template, title) { return String(template || '').replace('__PRODUCT__', title || ''); }
      syncButtons() {
        document.querySelectorAll('[data-drako-favorite-toggle]').forEach(button => {
          const saved = this.handles.includes(button.dataset.productHandle);
          button.setAttribute('aria-pressed', String(saved));
          button.setAttribute('aria-label', this.label(saved ? this.config.remove : this.config.add, button.dataset.productTitle));
          button.removeAttribute('hidden');
        });
        document.querySelectorAll('[data-drako-favorites-count]').forEach(count => { count.textContent = String(this.handles.length); count.dataset.count = String(this.handles.length); });
      }
      toggle(handle) {
        const existed = this.handles.includes(handle);
        if (!existed && this.handles.length >= 40) {
          if (this.feedback) this.feedback.textContent = this.config.limit;
          return;
        }
        this.handles = existed ? this.handles.filter(item => item !== handle) : [...this.handles, handle];
        const persisted = this.persist();
        this.syncButtons();
        if (this.feedback) this.feedback.textContent = persisted ? (existed ? this.config.removed : this.config.saved) : this.config.localOnly;
        if (existed && this.dialog?.open) {
          const row = Array.from(this.list?.children || []).find(item => item.dataset.handle === handle);
          const replacement = row?.nextElementSibling?.querySelector('a,button') || row?.previousElementSibling?.querySelector('a,button') || this.querySelector('[data-drako-favorites-close]');
          const restore = row?.contains(document.activeElement);
          row?.remove();
          if (restore) replacement?.focus();
          if (!this.handles.length && this.status) this.status.textContent = this.config.empty;
        }
      }
      endpoint(handle) {
        const root = new URL(this.dataset.root || '/', window.location.origin);
        if (root.origin !== window.location.origin) throw new Error('Invalid storefront root');
        const path = root.pathname.endsWith('/') ? root.pathname : `${root.pathname}/`;
        return new URL(`${path}products/${encodeURIComponent(handle)}.js`, root.origin);
      }
      async loadSaved() {
        if (!this.list || !this.status) return;
        this.requestController?.abort();
        this.requestController = new AbortController();
        const signal = this.requestController.signal;
        this.list.replaceChildren();
        this.status.textContent = this.handles.length ? this.config.loading : this.config.empty;
        const handles = [...this.handles];
        for (let offset = 0; offset < handles.length; offset += 4) {
          const entries = await Promise.all(handles.slice(offset, offset + 4).map(async handle => {
            try {
              const response = await fetch(this.endpoint(handle), { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
              if (!response.ok) return { handle, unavailable: response.status === 404, error: response.status !== 404 };
              const product = await response.json();
              const blocked = (this.config.blockedIds || []).includes(String(product.id));
              if (blocked || !validHandle(product.handle) || product.handle !== handle) return { handle, unavailable: true };
              return { handle, product };
            } catch (error) {
              if (error.name === 'AbortError') return null;
              return { handle, error: true };
            }
          }));
          if (signal.aborted || !this.isConnected) return;
          for (const entry of entries) {
            if (entry && this.handles.includes(entry.handle)) this.list.append(this.makeRow(entry));
          }
        }
        if (!signal.aborted) this.status.textContent = this.handles.length ? '' : this.config.empty;
        this.syncButtons();
      }
      money(price) {
        const value = Number(price);
        if (!Number.isFinite(value)) return '';
        try { return '$' + new Intl.NumberFormat('es-CL').format(Math.round(value / 100)); }
        catch { return ''; }
      }
      makeRow({ handle, product, unavailable, error }) {
        const row = document.createElement('article');
        row.className = 'drako-favorites__row';
        row.dataset.handle = handle;
        const copy = document.createElement('div');
        copy.className = 'drako-favorites__copy';
        if (product) {
          const href = this.endpoint(handle);
          href.pathname = href.pathname.slice(0, -3);
          const imageValue = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src;
          if (imageValue) {
              let imageUrl;
              try { imageUrl = new URL(imageValue, window.location.origin); } catch { imageUrl = null; }
            if (imageUrl && (imageUrl.protocol === 'https:' || imageUrl.origin === window.location.origin)) {
              const photoLink = document.createElement('a');
              photoLink.href = href.href;
              photoLink.tabIndex = -1;
              photoLink.setAttribute('aria-hidden', 'true');
              const image = document.createElement('img');
              image.src = imageUrl.href;
              image.alt = '';
              image.width = 78;
              image.height = 92;
              image.loading = 'lazy';
              photoLink.append(image);
              row.append(photoLink);
            }
          }
          const title = document.createElement('a');
          title.href = href.href;
          title.textContent = product.title;
          title.className = 'drako-favorites__title';
          copy.append(title);
          const price = document.createElement('p');
          price.className = 'drako-favorites__price';
          price.textContent = `${product.price_varies ? `${this.config.from} ` : ''}${this.money(product.price)}`;
          copy.append(price);
          if (!product.available) {
            const state = document.createElement('p');
            state.className = 'drako-favorites__unavailable';
            state.textContent = this.config.soldOut;
            copy.append(state);
          }
        } else {
          const state = document.createElement('p');
          state.textContent = error ? this.config.error : this.config.unavailable;
          copy.append(state);
        }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'drako-favorites__remove';
        remove.dataset.drakoFavoriteToggle = '';
        remove.dataset.productHandle = handle;
        remove.dataset.productTitle = product?.title || this.config.title;
        remove.textContent = this.label(this.config.remove, '').replace(/\s+/g, ' ').trim();
        remove.setAttribute('aria-label', this.label(this.config.remove, product?.title || this.config.title));
        remove.setAttribute('aria-pressed', 'true');
        copy.append(remove);
        row.append(copy);
        return row;
      }
    });
  }

  if (!customElements.get('drako-product-grid')) {
    customElements.define('drako-product-grid', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        const categoryQuery = new URL(window.location.href).searchParams.get('q');
        const collectionForm = this.querySelector('.drako-store-collection__form');
        if (categoryQuery && collectionForm && !collectionForm.querySelector('input[name="q"]')) {
          const query = document.createElement('input');
          query.type = 'hidden'; query.name = 'q'; query.value = categoryQuery;
          collectionForm.append(query);
        }
        this.addEventListener('click', event => {
          if (!(event.target instanceof Element)) return;
          const trigger = event.target.closest('[data-drako-quick-open]');
          if (trigger && this.contains(trigger)) {
            const dialog = document.getElementById(trigger.dataset.drakoQuickOpen);
            if (!dialog || !this.contains(dialog) || typeof dialog.showModal !== 'function') return;
            this.origin = trigger;
            this.updateVariant(dialog);
            dialog.showModal();
            dialog.querySelector('[data-drako-quick-close]')?.focus();
            return;
          }
          if (event.target.closest('[data-drako-quick-close]')) event.target.closest('dialog')?.close();
          const quantityButton = event.target.closest('[data-drako-quantity-change]');
          if (quantityButton) {
            const input = quantityButton.closest('form')?.querySelector('[data-drako-quick-quantity]');
            if (input) {
              const min = Number(input.min) || 1;
              const step = Number(input.step) || 1;
              const max = input.max ? Number(input.max) : Infinity;
              const current = Number(input.value) || min;
              input.value = String(Math.max(min, Math.min(max, current + Number(quantityButton.dataset.drakoQuantityChange) * step)));
              this.syncQuantity(input);
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          if (event.target instanceof HTMLDialogElement) {
            const rect = event.target.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
          }
        }, { signal });
        this.addEventListener('change', event => {
          if (!(event.target instanceof Element)) return;
          if (event.target.matches('[data-drako-quick-variant]')) this.updateVariant(event.target.closest('dialog'));
          if (event.target.matches('[data-drako-quick-quantity]')) this.syncQuantity(event.target);
        }, { signal });
        this.addEventListener('input', event => {
          if (event.target instanceof Element && event.target.matches('[data-drako-quick-quantity]')) this.syncQuantity(event.target);
        }, { signal });
        this.querySelectorAll('dialog').forEach(dialog => {
          this.updateVariant(dialog);
          dialog.addEventListener('close', () => { if (this.origin?.isConnected) this.origin.focus(); }, { signal });
        });
        document.dispatchEvent(new CustomEvent('drako:cards-ready'));
      }
      disconnectedCallback() { this.controller?.abort(); this.controller = null; }
      updateVariant(dialog) {
        if (!dialog) return;
        const data = parseJSON(dialog.querySelector('[data-drako-quick-data]'));
        const selector = dialog.querySelector('[data-drako-quick-variant]');
        const variant = data.variants?.find(item => String(item.id) === selector?.value);
        const add = dialog.querySelector('[data-drako-quick-add]');
        const status = dialog.querySelector('[data-drako-quick-status]');
        if (!variant) { if (add) add.disabled = true; return; }
        if (add) { add.disabled = !variant.available; add.textContent = data.addLabel; }
        if (status) status.textContent = variant.available ? '' : data.soldOut;
        const price = dialog.querySelector('[data-drako-quick-price]');
        if (price) price.textContent = variant.price;
        const image = dialog.querySelector('.drako-store-quick__image');
        if (image && variant.image) {
          image.removeAttribute('srcset');
          image.src = variant.image;
          image.alt = variant.imageAlt;
        }
        const input = dialog.querySelector('[data-drako-quick-quantity]');
        if (input) {
          const min = Number(variant.min) || 1;
          const step = Number(variant.step) || 1;
          input.min = String(min);
          input.step = String(step);
          if (variant.max != null) input.max = String(variant.max); else input.removeAttribute('max');
          const max = input.max ? Number(input.max) : Infinity;
          const current = Number(input.value) || min;
          input.value = String(Math.min(max, min + Math.max(0, Math.ceil((current - min) / step)) * step));
          this.syncQuantity(input);
        }
        const details = dialog.querySelector('[data-drako-quick-details]');
        if (details) {
          const url = new URL(details.href, window.location.origin);
          url.searchParams.set('variant', String(variant.id));
          details.href = url.href;
        }
      }
      syncQuantity(input) {
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const value = Number(input.value);
        input.closest('.drako-store-quantity')?.querySelectorAll('[data-drako-quantity-change]').forEach(button => {
          button.removeAttribute('hidden');
          button.disabled = Number(button.dataset.drakoQuantityChange) < 0 ? value <= min : value >= max;
        });
      }
    });
  }
})();
